import { Button, Card, CardContent, Grid, Stack, styled, Typography } from "@mui/material";
import useLabelPrintRequest from "hooks/useLabelPrintRequest";
import { useCallback, useEffect, useMemo } from "react";
import { BoxType } from "types/assetRetrieval";
import { LabelComponentInfo, LabelPrintRequestStatus } from "types/labelPrintRequest";
import { humanize, pluralize } from "utils/helpers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";

const CompletedCard = ({ boxType }: { boxType: BoxType }) => {
  return (
    <Card variant="outlined">
      <CardContent sx={{ px: 3, py: 2, minHeight: "251.5px", backgroundColor: "rgba(237, 255, 245, 0.7)" }}>
        <Stack spacing={4}>
          <Typography>{humanize(boxType)} Boxes</Typography>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 100, color: "success.main" }} />
          </div>
          <div />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function BoomerangPrintComponent({
  labelComponentData,
  boxType,
}: {
  labelComponentData?: LabelComponentInfo;
  boxType: BoxType;
}) {
  const { printLabels, isPrintingLabels, labelPrintRequestData, setLabelPrintRequestId } = useLabelPrintRequest();

  const handlePrintLabels = useCallback(async () => {
    const labelPrintRequest = await printLabels({ box_type: boxType });
    setLabelPrintRequestId(labelPrintRequest.id);
  }, [printLabels, setLabelPrintRequestId, boxType]);

  const isCompleted = useMemo(() => {
    return (
      (labelComponentData?.completedCount || 0) > 0 &&
      (labelComponentData?.pendingCount || 0) === 0 &&
      (labelComponentData?.nonProcessableOrders.length || 0) === 0
    );
  }, [labelComponentData]);

  const StyledLink = styled(Link)({
    color: "red",
    textDecoration: "none",
    ":hover": {
      textDecoration: "underline",
    },
    ":-webkit-any-link": {
      color: "red",
    },
  });

  useEffect(() => {
    if (labelPrintRequestData?.state === LabelPrintRequestStatus.completed) {
      if (labelPrintRequestData?.assetRetrievalsCount && labelPrintRequestData.assetRetrievalsCount > 0) {
        window.open(labelPrintRequestData.labelUrl, "_blank");
      }

      setLabelPrintRequestId(null);
    }
  }, [
    labelPrintRequestData?.state,
    labelPrintRequestData?.assetRetrievalsCount,
    labelPrintRequestData?.labelUrl,
    setLabelPrintRequestId,
  ]);

  if (isCompleted) {
    return <CompletedCard boxType={boxType} />;
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ px: 3, py: 2 }}>
        <Grid container rowSpacing={5}>
          <Grid item xs={6}>
            <Grid item xs={12}>
              <Typography>{humanize(boxType)} Boxes</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h2" fontWeight={550}>
                {labelComponentData?.pendingCount || 0}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary">
                To Fulfill
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={6} display="flex" alignItems="center" justifyContent="end" minHeight={175}>
            <img src={`/${boxType}_box.svg`} alt={boxType} />
          </Grid>
          <Grid item xs={7} sx={{ paddingTop: "34px" }}>
            {(labelComponentData?.completedCount || 0) > 0 && (
              <Typography
                variant="body1"
                fontSize={13.5}
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={1}
              >
                {pluralize(labelComponentData?.completedCount || 0, "order", "orders")} bulk printed
              </Typography>
            )}
            {(labelComponentData?.nonProcessableOrders.length || 0) > 0 && (
              <StyledLink
                to={`/support/asset_retrievals?filter[]=order_number,is_any_of,${labelComponentData?.nonProcessableOrders.map((order) => order.orderNumber).join(",")}`}
              >
                {labelComponentData?.nonProcessableOrders.length} with label issues
              </StyledLink>
            )}
          </Grid>
          <Grid item xs={5} display="flex" alignItems="end" justifyContent="end">
            <Button
              variant="contained"
              size="medium"
              color="primary"
              sx={{ width: "150px" }}
              onClick={handlePrintLabels}
              disabled={isPrintingLabels || !!labelPrintRequestData || !labelComponentData?.pendingCount}
            >
              {labelPrintRequestData ? "Printing..." : "Print"}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
